// frontend/src/components/tickets/TicketRelatedTasksCard.jsx
//
// "Tareas relacionadas" dentro del detalle del ticket.
//
// Antes esta información solo vivía en el panel "Acciones e IA", y allí apenas
// se sabía si YA existía una tarea. Aquí se ve el trabajo real asociado al
// ticket: qué hay que hacer, quién lo tiene y para cuándo.

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as TaskIcon,
  OpenInNew as OpenIcon,
  Person as PersonIcon,
  Event as DueIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { api } from '../../services/api';
import { formatDuration } from '../../utils/duration';

const STATUS_META = {
  pending: { label: 'Pendiente', color: 'default' },
  in_progress: { label: 'En progreso', color: 'info' },
  blocked: { label: 'Bloqueada', color: 'warning' },
  completed: { label: 'Completada', color: 'success' },
  cancelled: { label: 'Cancelada', color: 'default' },
};

const PRIORITY_META = {
  low: { label: 'Baja', color: 'default' },
  medium: { label: 'Media', color: 'info' },
  high: { label: 'Alta', color: 'warning' },
  critical: { label: 'Crítica', color: 'error' },
};

const TicketRelatedTasksCard = ({ ticketId, refreshKey = 0, formatTimestamp }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!ticketId) return;
    setLoading(true);
    api.alarmTickets.getTicketTasks(ticketId)
      .then((res) => setTasks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Se escucha el evento que ya emite el centro de acciones al crear tareas,
  // para no duplicar la lógica de refresco.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener('aps-ticket-collab-changed', onChanged);
    return () => window.removeEventListener('aps-ticket-collab-changed', onChanged);
  }, [load]);

  const dueLabel = (task) => {
    if (!task.due_at) return 'Sin fecha límite';
    const text = formatTimestamp ? formatTimestamp(task.due_at) : new Date(task.due_at).toLocaleString();
    return task.is_overdue ? `${text} · vencida` : text;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <TaskIcon color="action" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
          Tareas relacionadas
        </Typography>
        {!loading && <Chip size="small" variant="outlined" label={tasks.length} />}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay tareas relacionadas con este ticket.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={1}>
          {tasks.map((task) => {
            const status = STATUS_META[task.status] || { label: task.status, color: 'default' };
            const priority = PRIORITY_META[task.priority] || { label: task.priority, color: 'default' };
            return (
              <Box
                key={task.id}
                onClick={() => navigate(`/tasks/${task.id}`)}
                sx={{ pt: 0.5, cursor: 'pointer', '&:hover .task-title': { textDecoration: 'underline' } }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Typography className="task-title" variant="body2" fontWeight={700} sx={{ flexGrow: 1, minWidth: 0 }}>
                    {task.title}
                  </Typography>
                  <Tooltip title="Abrir tarea"><OpenIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></Tooltip>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" color={status.color} label={status.label} sx={{ height: 20, fontSize: '0.68rem' }} />
                  <Chip size="small" color={priority.color} variant="outlined" label={priority.label}
                    sx={{ height: 20, fontSize: '0.68rem' }} />
                  <Stack direction="row" spacing={0.4} alignItems="center">
                    <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      {task.assignee_name || 'Sin responsable'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.4} alignItems="center">
                    <DueIcon sx={{ fontSize: 14, color: task.is_overdue ? 'error.main' : 'text.disabled' }} />
                    <Typography variant="caption" color={task.is_overdue ? 'error.main' : 'text.secondary'}>
                      {dueLabel(task)}
                    </Typography>
                  </Stack>
                  {task.created_at && (
                    <Typography variant="caption" color="text.disabled">
                      · creada hace {formatDuration(Date.now() - new Date(task.created_at).getTime(), 'ms')}
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

TicketRelatedTasksCard.propTypes = {
  ticketId: PropTypes.string.isRequired,
  refreshKey: PropTypes.number,
  formatTimestamp: PropTypes.func,
};

export default TicketRelatedTasksCard;
